import { useCallback, useEffect, useState } from "react";
import { ArrowClockwise, Gauge, MapPin, Star, Truck } from "@phosphor-icons/react";
import { AppShell } from "@/components/AppShell";
import { AddFacilityForm } from "@/components/AddFacilityForm";
import { FacilityCard } from "@/components/FacilityCard";
import { FacilityMap } from "@/components/FacilityMap";
import { FiltersBar } from "@/components/FiltersBar";
import { fetchFacilities, fetchStats } from "@/lib/api";

const emptyFilters = { q: "", state: "", service: "", min_rating: "" };

const StatTile = ({ icon: Icon, label, value, testId }) => (
  <div data-testid={testId} className="border-l border-white/20 pl-4">
    <Icon data-testid={`${testId}-icon`} size={24} weight="fill" className="text-[#FF5500]" />
    <p data-testid={`${testId}-value`} className="mt-2 font-display text-2xl font-black tracking-normal text-white">{value}</p>
    <p data-testid={`${testId}-label`} className="text-xs font-bold uppercase tracking-[0.18em] text-white/70">{label}</p>
  </div>
);

const DirectoryHero = ({ stats }) => (
  <section data-testid="hero-section" className="relative overflow-hidden bg-[#09090B] text-white">
    <img
      data-testid="hero-background-image"
      src="https://static.prod-images.emergentagent.com/jobs/62d263a8-aaad-4810-bdde-38051f3e2d7b/images/6ec3f67a44e90a3571bfbbd7ada938bc54824edd8e9f3edec1d265ab8e44c4e1.png"
      alt="Modern DTNA Elite Support facility"
      className="absolute inset-0 h-full w-full object-cover object-center opacity-45"
    />
    <div data-testid="hero-overlay" className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/20" />
    <div className="relative mx-auto grid max-w-[1600px] gap-8 px-4 py-12 sm:px-6 lg:grid-cols-[1.2fr_0.8fr] lg:px-8 lg:py-16">
      <div data-testid="hero-copy" className="max-w-4xl">
        <p data-testid="hero-eyebrow" className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF5500]">US Elite Support locator</p>
        <h1 data-testid="hero-title" className="mt-4 font-display text-4xl font-black leading-none tracking-normal text-white sm:text-5xl lg:text-6xl">
          Find DTNA Elite Support facilities with live customer ratings.
        </h1>
        <p data-testid="hero-description" className="mt-5 max-w-2xl text-base leading-7 text-white/78 md:text-lg">
          Search a Elite Support ready nationwide facility directory, compare service capabilities, and review customer feedback from fleet operators on the go.
        </p>
      </div>
      <div data-testid="hero-stats" className="grid grid-cols-2 gap-5 self-end border border-white/15 bg-white/10 p-5 backdrop-blur-md">
        <StatTile testId="stat-total-facilities" icon={Truck} label="Facilities" value={stats?.total_facilities ?? "—"} />
        <StatTile testId="stat-states-covered" icon={MapPin} label="States" value={stats?.states_covered ?? "—"} />
        <StatTile testId="stat-average-rating" icon={Star} label="Avg rating" value={stats?.average_rating ?? "—"} />
        <StatTile testId="stat-total-reviews" icon={Gauge} label="Reviews" value={stats?.total_reviews ?? "—"} />
      </div>
    </div>
  </section>
);

const FacilityList = ({ facilities, activeFacility, loading, error, onSelect }) => (
  <div data-testid="facility-list" className="facility-scrollbar flex h-[70vh] flex-col gap-3 overflow-y-auto pr-1 md:h-[72vh]">
    {loading && <p data-testid="facility-list-loading" className="border border-zinc-200 bg-white p-4 font-bold text-[#52525B]">Loading facilities...</p>}
    {error && <p data-testid="facility-list-error" className="border border-red-200 bg-red-50 p-4 font-bold text-red-700">{error}</p>}
    {!loading && !error && facilities.length === 0 && (
      <p data-testid="facility-list-empty" className="border border-zinc-200 bg-white p-4 font-bold text-[#52525B]">No facilities match these filters.</p>
    )}
    {!loading && !error && facilities.map((facility, index) => (
      <FacilityCard key={facility.id} facility={facility} active={activeFacility?.id === facility.id} onSelect={onSelect} index={index} />
    ))}
  </div>
);

const DirectoryMapSection = ({ facilities, activeFacility, loading, error, loadData, onSelect }) => (
  <section data-testid="directory-map-section" className="technical-grid px-4 py-6 sm:px-6 lg:px-8">
    <div className="mx-auto grid max-w-[1600px] grid-cols-1 gap-6 md:grid-cols-12">
      <div data-testid="map-column" className="md:col-span-8">
        <FacilityMap facilities={facilities} activeFacility={activeFacility} onSelect={onSelect} />
      </div>
      <aside data-testid="directory-column" className="md:col-span-4">
        <div className="mb-3 flex items-center justify-between gap-4">
          <div>
            <p data-testid="directory-eyebrow" className="text-xs font-bold uppercase tracking-[0.18em] text-[#0055FF]">Directory</p>
            <h2 data-testid="directory-title" className="font-display text-2xl font-black tracking-normal">{facilities.length} matching facilities</h2>
          </div>
          <button
            data-testid="refresh-directory-button"
            onClick={loadData}
            type="button"
            className="grid h-11 w-11 place-items-center rounded-md border border-zinc-300 bg-white text-[#09090B] transition-colors duration-200 hover:border-[#0055FF] hover:text-[#0055FF]"
          >
            <ArrowClockwise data-testid="refresh-directory-icon" size={20} weight="bold" />
          </button>
        </div>
        <FacilityList facilities={facilities} activeFacility={activeFacility} loading={loading} error={error} onSelect={onSelect} />
      </aside>
    </div>
  </section>
);

const getFilterOptions = (facilities) => ({
  states: [...new Set(facilities.map((facilityItem) => facilityItem.state))].sort(),
  services: [...new Set(facilities.flatMap((facilityItem) => facilityItem.services))].sort(),
});

export default function DirectoryPage() {
  const [facilities, setFacilities] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState(emptyFilters);
  const [activeFacility, setActiveFacility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [facilityData, statsData] = await Promise.all([fetchFacilities(filters), fetchStats()]);
      const nextActiveFacility = facilityData.find((facilityItem) => facilityItem.id === activeFacility?.id) || facilityData[0] || null;
      setFacilities(facilityData);
      setStats(statsData);
      setActiveFacility(nextActiveFacility);
    } catch {
      setError("Facility network data is temporarily unavailable.");
    } finally {
      setLoading(false);
    }
  }, [activeFacility?.id, filters, setActiveFacility, setError, setFacilities, setLoading, setStats]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const { states, services } = getFilterOptions(facilities);

  return (
    <AppShell>
      <DirectoryHero stats={stats} />

      <FiltersBar
        filters={filters}
        setFilters={setFilters}
        states={states}
        services={services}
        onClear={() => setFilters(emptyFilters)}
      />

      <DirectoryMapSection facilities={facilities} activeFacility={activeFacility} loading={loading} error={error} loadData={loadData} onSelect={setActiveFacility} />

      <AddFacilityForm />
    </AppShell>
  );
}